import { AnyAction } from 'redux';
import cloneDeep from 'lodash/cloneDeep';
import { App, IAnimalValueLabelElement, ValueLabelElement } from '@ps20-frontend/frontend-models';
import {
  GlobalActions,
  ProduktDeleted,
  TkvV2CheckoutProduktPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  AnimalIllness, DiagnoseVorvertrag,
  InitialHealthStatusEntry,
  KennzeichnungsArt,
  Tierart,
  Tiergeschlecht,
  Tierhaltung,
} from '@ps20/bff-models';
import { IAppState } from '@ps20-frontend/apps/checkout-store';
import { v1 as uuidv1 } from 'uuid';
import { HealthStatusEntry, HealthStatusList } from '../model/healthStatus';
import { CheckoutActions } from '../checkout.actions';
import {
  TkvAngabenV2BooleanPayload,
  TkvAngabenV2DiagnosisDescriptionPayload,
  TkvAngabenV2DiagnosisDetailsPayload,
  TkvAngabenV2DiagnosisPayload,
  TkvAngabenV2HealthStatusListPayload,
  TkvAngabenV2IdentificationTypePayload,
  TkvAngabenV2StringPayload,
  TkvAngabenV2TreatmentStatusPayload,
} from '../action-types/tkvAngabenV2Payload';
import { filterHealthStatusListForAnimalType } from '../../utils/health-status.utils';

export interface TkvAngabenV2 {
  angabenItems: TkvAngabenV2Item[];
  healthStatusList?: HealthStatusList;
  dogBreeds?: IAnimalValueLabelElement[];
  horseBreeds?: ValueLabelElement[];
  catBreeds?: ValueLabelElement[];
}

export interface TkvAngabenV2Item {
  angebotsId: string;
  animalType: Tierart;
  animalGender: Tiergeschlecht;
  animalName: string;
  animalBirthDate: string;
  breed: ValueLabelElement;
  catKeepingType?: Tierhaltung;
  hasIllnesses: boolean;
  selectedIllnesses?: AnimalIllness[];
  animalIdentificationType?: KennzeichnungsArt;
  animalIdentification?: string;
  isAssistanceDog?: boolean;
  healthStatusEntries?: HealthStatusEntry[];
  previousHealthStatusEntries?: HealthStatusEntry[];
  disabledFields: DisabledFields;
}

export interface DisabledFields {
  name: boolean;
  kennzeichnung: boolean;
}

export const TKV_TIERNAME_REGEXP = String.raw`^[a-zäöüÄÖÜßéèêàáâ#:?!´/=+@$(),A-Z0-9.'\- ]{0,50}$`;
export const TKV_LEBENSNUMMER_REGEXP = '^[A-Za-z0-9äöüÄÖÜß ]{1,15}$';
export const TKV_CHIP_REGEXP = String.raw`^\d{15}$`;
export const TKV_TAETOWIERUNG_REGEXP = '^[A-Za-z0-9äöüÄÖÜß]{1,20}$';

const findEntryIndex = (sectionState: TkvAngabenV2, angebotsId: string): number | null => {
  const index = sectionState.angabenItems.findIndex(entry => entry.angebotsId === angebotsId);
  return index >= 0 ? index : null;
};

function produktAction(sectionState: TkvAngabenV2, action: AnyAction): TkvAngabenV2 {
  const produkt: TkvV2CheckoutProduktPayload = action.payload;

  if (produkt.appId !== App.TKV_V2) {
    return sectionState;
  }

  const newState: TkvAngabenV2 = cloneDeep(sectionState);
  const entryIndex = findEntryIndex(sectionState, produkt.angebotsId);
  const existingEntry: TkvAngabenV2Item = sectionState.angabenItems.find(
    entry => entry.angebotsId === produkt.angebotsId,
  );
  const healthStatusEntries: HealthStatusEntry[] = produkt.selectedIllnesses?.flatMap(illness => {
    const existingHealthEntries = existingEntry?.healthStatusEntries?.filter(
      entry => entry.area === illness.name,
    ) || [];
    return existingHealthEntries.length > 0
      ? existingHealthEntries
      : [createHealthStatusEntry(illness.name)];
  }) || [];
  const healthStatusListForAnimalType = filterHealthStatusListForAnimalType(
    produkt.animalType,
    sectionState.healthStatusList
  );
  const previousHealthStatusEntries: HealthStatusEntry[] =
    produkt.previousDiagnoses?.map(diagnosis =>
      createPreviousHealthStatusEntry(diagnosis, healthStatusListForAnimalType)
    ) || [];
  const newEntry: TkvAngabenV2Item = {
    ...existingEntry,
    angebotsId: produkt.angebotsId,
    animalType: produkt.animalType,
    animalGender: produkt.animalGender,
    animalName: produkt.animalName,
    animalBirthDate: produkt.animalBirthDate,
    breed: produkt.breed,
    catKeepingType: produkt.catKeepingType,
    hasIllnesses: produkt.hasIllnesses || previousHealthStatusEntries.length > 0,
    selectedIllnesses: produkt.selectedIllnesses,
    healthStatusEntries,
    previousHealthStatusEntries,
    isAssistanceDog: produkt.isAssistanceDog ?? existingEntry?.isAssistanceDog,
  };

  if (produkt.animalIdentification && !existingEntry?.animalIdentification) {
    newEntry.animalIdentification = produkt.animalIdentification;
  }
  if (produkt.animalIdentificationType && !existingEntry?.animalIdentificationType) {
    newEntry.animalIdentificationType = produkt.animalIdentificationType;
  }

  const isErsatzAndNotBeibehalten =
    produkt.vorvertragsnummer?.length > 0 && !produkt.beibehalten;

  newEntry.disabledFields = {
    name: Boolean(
      produkt.animalName &&
        new RegExp(TKV_TIERNAME_REGEXP).exec(produkt.animalName) !== null &&
        !produkt.isVnWechsel &&
        isErsatzAndNotBeibehalten,
    ),
    kennzeichnung: Boolean(
      produkt.animalIdentificationType !== KennzeichnungsArt.KEINE &&
        produkt.animalIdentification &&
        kennzeichenMatchRegExp(
          produkt.animalIdentificationType,
          produkt.animalIdentification,
        ) &&
        isErsatzAndNotBeibehalten,
    ),
  };

  if (entryIndex === null) {
    newState.angabenItems.push(newEntry);
  } else {
    newState.angabenItems[entryIndex] = newEntry;
  }
  return newState;
}

const updateAngabenItem = (
  sectionState: TkvAngabenV2,
  angebotsId: string,
  update: (item: TkvAngabenV2Item) => void,
): TkvAngabenV2 => {
  const index = findEntryIndex(sectionState, angebotsId);
  if (index === null) {
    return sectionState;
  }
  const newState: TkvAngabenV2 = cloneDeep(sectionState);
  update(newState.angabenItems[index]);
  return newState;
};

export const tkvAngabenV2Reducer = function (
  sectionState: TkvAngabenV2 = null,
  action: AnyAction,
  state: IAppState,
): TkvAngabenV2 {
  switch (action.type) {
    case GlobalActions.PRODUKT_ACTION:
      return produktAction(sectionState, action);
    case CheckoutActions.TKV_V2_ASSISTANCE_DOG_CHANGED: {
      const payload: TkvAngabenV2BooleanPayload = action.payload;
      return updateAngabenItem(sectionState, payload.angebotsId, item => {
        item.isAssistanceDog = payload.value;
      });
    }
    case CheckoutActions.TKV_V2_HEALTH_STATUS_LIST_DOG_CAT_RECEIVED: {
      const payload: TkvAngabenV2HealthStatusListPayload = action.payload;
      return {
        ...sectionState,
        healthStatusList: {
          ...sectionState.healthStatusList,
          dogCat: payload.initialHealthStatusEntries,
        },
      };
    }
    case CheckoutActions.TKV_V2_HEALTH_STATUS_LIST_HORSE_RECEIVED: {
      const payload: TkvAngabenV2HealthStatusListPayload = action.payload;
      return {
        ...sectionState,
        healthStatusList: {
          ...sectionState.healthStatusList,
          horse: payload.initialHealthStatusEntries,
        },
      };
    }
    case CheckoutActions.TKV_V2_HEALTH_STATUS_ENTRY_ADDED: {
      const payload: TkvAngabenV2StringPayload = action.payload;
      return updateAngabenItem(sectionState, payload.angebotsId, item => {
        item.healthStatusEntries.push(createHealthStatusEntry(payload.value));
      });
    }
    case CheckoutActions.TKV_V2_HEALTH_STATUS_ENTRY_REMOVED: {
      const payload: TkvAngabenV2StringPayload = action.payload;
      return updateAngabenItem(sectionState, payload.angebotsId, item => {
        item.healthStatusEntries = item.healthStatusEntries.filter(
          entry => entry.id !== payload.value,
        );
      });
    }
    case CheckoutActions.TKV_V2_DIAGNOSIS_CHANGED: {
      const payload: TkvAngabenV2DiagnosisPayload = action.payload;
      return updateAngabenItem(sectionState, payload.angebotsId, item => {
        const healthStatusList = filterHealthStatusListForAnimalType(
          payload.animalType,
          state.tkvAngabenV2.healthStatusList,
        );
        const initialHealthStatusEntry = healthStatusList.find(
          entry => entry.diagnosis === payload.diagnosis,
        );
        const currentEntry = item.healthStatusEntries.find(entry => entry.id === payload.id);

        currentEntry.diagnosis = payload.diagnosis;
        currentEntry.details = initialHealthStatusEntry.details;
        currentEntry.isTreatable = initialHealthStatusEntry.isTreatable;
        currentEntry.notInsuredText = initialHealthStatusEntry.notInsuredText;
        currentEntry.notInsuredScopes = initialHealthStatusEntry.notInsuredScopes;
        currentEntry.isNotInsuredIfHealed = initialHealthStatusEntry.isNotInsuredIfHealed;
        currentEntry.hasDescription = initialHealthStatusEntry.hasDescription;
        currentEntry.selectedDetails = [];
        currentEntry.treatmentStatus = currentEntry.details ? [] : [{ isHealed: null }];
      });
    }
    case CheckoutActions.TKV_V2_DIAGNOSIS_DETAILS_CHANGED: {
      const payload: TkvAngabenV2DiagnosisDetailsPayload = action.payload;
      return updateAngabenItem(sectionState, payload.angebotsId, item => {
        const currentEntry = item.healthStatusEntries.find(entry => entry.id === payload.id);
        currentEntry.selectedDetails = payload.selectedDetails;
        currentEntry.treatmentStatus = currentEntry.treatmentStatus.filter(status =>
          currentEntry.selectedDetails.includes(status.selectedDetail),
        );
        currentEntry.selectedDetails.forEach(selectedDetail => {
          if (
            !currentEntry.treatmentStatus.some(status => status.selectedDetail === selectedDetail)
          ) {
            currentEntry.treatmentStatus.push({ selectedDetail, isHealed: null });
          }
        });
      });
    }
    case CheckoutActions.TKV_V2_DIAGNOSIS_DESCRIPTION_CHANGED: {
      const payload: TkvAngabenV2DiagnosisDescriptionPayload = action.payload;
      return updateAngabenItem(sectionState, payload.angebotsId, item => {
        const currentEntry = item.healthStatusEntries.find(entry => entry.id === payload.id);
        currentEntry.description = payload.description;
      });
    }
    case CheckoutActions.TKV_V2_TREATMENT_STATUS_CHANGED: {
      const payload: TkvAngabenV2TreatmentStatusPayload = action.payload;
      return updateAngabenItem(sectionState, payload.angebotsId, item => {
        const currentEntry = item.healthStatusEntries.find(entry => entry.id === payload.id);
        currentEntry.treatmentStatus.find(
          status => status.selectedDetail === payload.selectedDetail,
        ).isHealed = payload.isHealed;
      });
    }
    case GlobalActions.HUNDERASSENLISTE_RETRIEVED: {
      const dogBreeds: IAnimalValueLabelElement[] = action.payload;
      return { ...sectionState, dogBreeds };
    }
    case GlobalActions.PFERDERASSEN_LISTE_RETRIEVED: {
      const horseBreeds: ValueLabelElement[] = action.payload;
      return { ...sectionState, horseBreeds };
    }
    case GlobalActions.KATZENRASSEN_LISTE_RETRIEVED: {
      const catBreeds: ValueLabelElement[] = action.payload;
      return { ...sectionState, catBreeds };
    }
    case GlobalActions.PRODUKT_DELETED: {
      const produktDeletedPayload: ProduktDeleted = action.payload;
      const newState: TkvAngabenV2 = cloneDeep(sectionState);

      for (const angebot of produktDeletedPayload.angebotsIds.filter(
        angebotsId => angebotsId.appId === App.TKV_V2,
      )) {
        const index = findEntryIndex(newState, angebot.angebotsId);
        if (index !== null) {
          newState.angabenItems.splice(index, 1);
        }
      }

      return newState;
    }
    case CheckoutActions.TKV_V2_IDENTIFICATION_TYPE_CHANGED: {
      const payload: TkvAngabenV2IdentificationTypePayload = action.payload;
      return updateAngabenItem(sectionState, payload.angebotsId, item => {
        item.animalIdentificationType = payload.value;
        item.animalIdentification = '';
      });
    }
    case CheckoutActions.TKV_V2_IDENTIFICATION_CHANGED: {
      const payload: TkvAngabenV2StringPayload = action.payload;
      return updateAngabenItem(sectionState, payload.angebotsId, item => {
        item.animalIdentification = payload.value;
      });
    }
    default:
      return sectionState;
  }
};

function createHealthStatusEntry(area: string): HealthStatusEntry {
  return {
    id: uuidv1(),
    area,
    diagnosis: null,
    notInsuredText: null,
    notInsuredScopes: [],
    selectedDetails: [],
    treatmentStatus: [],
  };
}

function createPreviousHealthStatusEntry(
  diagnosis: DiagnoseVorvertrag,
  healthStatusList: InitialHealthStatusEntry[],
): HealthStatusEntry {
  let notInsuredText = diagnosis.notInsuredText;

  if (diagnosis.sourcePlatform === 'PS20' && diagnosis.diagnose && healthStatusList) {
    const matchingEntry = healthStatusList.find(entry =>
      entry.notInsuredScopes?.some(scope => scope.code === diagnosis.diagnose)
    );
    if (matchingEntry) {
      notInsuredText = diagnosis.notInsuredText || matchingEntry.notInsuredText;
    }
  }

  return {
    id: uuidv1(),
    area: 'Vorvertrag',
    diagnosis: diagnosis.diagnoseBeschreibung || diagnosis.diagnose,
    description: diagnosis.diagnoseBeschreibung,
    notInsuredText,
    notInsuredScopes: diagnosis.diagnose ? [{
      code: diagnosis.diagnose,
      scope: diagnosis.diagnoseDetail || 'default'
    }] : [],
    selectedDetails: diagnosis.diagnoseDetail ? [diagnosis.diagnoseDetail] : [],
    treatmentStatus: [],
    isTreatable: false,
    isPreviousContract: true,
  };
}

function kennzeichenMatchRegExp(
  kennzeichnungsArt: KennzeichnungsArt,
  kennzeichen: string,
): boolean {
  switch (kennzeichnungsArt) {
    case KennzeichnungsArt.CHIP:
      return new RegExp(TKV_CHIP_REGEXP).exec(kennzeichen) !== null;
    case KennzeichnungsArt.LEBENSNUMMER:
      return new RegExp(TKV_LEBENSNUMMER_REGEXP).exec(kennzeichen) !== null;
    case KennzeichnungsArt.TAETOWIERUNG:
      return new RegExp(TKV_TAETOWIERUNG_REGEXP).exec(kennzeichen) !== null;
    default:
      return true;
  }
}
